import { Fragment, PureComponent } from 'react'
import PropTypes from 'prop-types'
import { withTranslation } from 'react-i18next'
import { AllHtmlEntities } from 'html-entities'
import moment from 'moment'
import 'moment-timezone'
import { get, isUndefined } from 'lodash'

import Typography from 'beam/dist/Typography'
import {
  CompactExpansionPanel,
  CompactExpansionPanelSummary,
  CompactExpansionPanelDetails,
} from 'beam/dist/CompactExpansionPanel'
import BeamIcon from 'beam/dist/BeamIcon'
import Button from 'beam/dist/Button'
import withStyles from 'beam/dist/styles'
import { CircularProgress } from 'beam/dist/Progress'
import { toHumanReadable } from 'beam/dist/utils/duration'

import FindSimilar from './FindSimilar'
import AssociatedContacts from './AssociatedContacts'
import PostCallSurvey from './PostCallSurvey'
import MetadataEditor from './MetadataEditor'

import Sentiment from '../const/sentiment'
import { CONTACT_TYPE } from '../MediaPlayer/mediaPlayer.constants'
import EVAL_RESPONSE_STATE from '../const/evaluationResponseState'
import { formatMediaDisplayState } from '../helpers'

import { authenticatedUserShape, PERMISSIONS, withAuthenticatedUser } from '../../../Permissions'
import { EVAL_STATE } from '../SingleEvaluationForm/evaluationForm.constants'
import { withFeatureFlags, FEATURE_FLAGS } from '../../../FeatureFlags'

const autoQaIdPrefix = 'App-Recordings-MediaPlayer-Explore-'

const entities = new AllHtmlEntities()

const styles = theme => ({
  sectionTitle: {
    paddingBottom: theme.spacing(2),
  },
  expansionRoot: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    boxShadow: 'none',
    background: 'none',
    '&:before': {
      display: 'none',
    },
  },
  expansionSummaryRoot: {
    padding: theme.spacing(0, 1),
    background: 'none',
  },
  expansionSummaryExpanded: {
    background: 'none',
  },
  expansionDetailsRoot: {
    padding: theme.spacing(1, 0, 0),
    background: 'none',
  },
  exploreExpansionDetails: {
    padding: 0,
  },
  contactInfoSection: {
    marginBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  contactInfoItem: {
    display: 'flex',
  },
  contactInfoTitle: {
    fontWeight: 700,
  },
  contactInfoLabel: {
    flex: '0 0 auto',
    display: 'inline-block',
    marginRight: theme.spacing(1),
    width: theme.spacing(13),
  },
  contactInfoValue: {
    flex: '1 1 auto',
  },
  details: {
    flex: '1 1 auto',
    display: 'flex',
    flexWrap: 'wrap',
  },
  detailsList: {
    flex: `0 0 ${theme.spacing(21)}px`,
    padding: theme.spacing(1),
    paddingTop: 0,
    paddingBottom: theme.spacing(2),
  },
  detailKey: {
    display: 'block',
  },
  center: {
    margin: 'auto auto',
  },
  findSimilarContainer: {
    padding: theme.spacing(0, 1, 1, 1),
  },
  goalInstructions: {
    whiteSpace: 'pre-wrap',
  },
})

const evalScoreMap = {
  0: 'unscored',
  1: 'scored',
  2: 'inProgress',
  3: 'needsApproval',
}

const evalResponseStateMap = {
  [EVAL_RESPONSE_STATE.PENDING]: 'pendingResponse',
  [EVAL_RESPONSE_STATE.ACKNOWLEDGED]: 'acknowledged',
  [EVAL_RESPONSE_STATE.APPEALED]: 'appealed',
}
class ExploreContent extends PureComponent {
  static propTypes = {
    contact: PropTypes.object.isRequired,
    file: PropTypes.object,
    classes: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired,
    openContact: PropTypes.func.isRequired,
    authenticatedUser: authenticatedUserShape.isRequired,
    handleMetadataSubmission: PropTypes.func,
    hasDx: PropTypes.bool,
    timezoneOverride: PropTypes.string,
    loading: PropTypes.bool.isRequired,
    error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]).isRequired,
    canUseFeature: PropTypes.func,
  }

  static defaultProps = {
    file: {},
    handleMetadataSubmission: () => {},
    hasDx: false,
  }

  state = {
    isFindSimilarOpen: false,
  }

  contactDetail = (key, value, condition) => {
    const { t } = this.props

    if (typeof condition !== 'undefined' && !condition) return
    return { key, label: t(key), value }
  }

  showGoalInstructions = () => {
    const {
      contact,
      authenticatedUser: { hasPermission },
    } = this.props

    return hasPermission(PERMISSIONS.VIEW_CONTACT_GOAL) && contact.goal !== null && contact.goal.instruction !== ''
  }

  mapContactDetails = () => {
    const {
      authenticatedUser: { hasPermission },
    } = this.props

    return [
      this.mapContactOrganizationDetails(),
      this.mapContactEvaluationDetails(),
      this.mapContact(),
      this.mapContactSpeechDetails(),
      this.mapContactAnalyticsDetails(hasPermission(PERMISSIONS.VIEW_CAA_IN_RECORDING_APP)),
    ]
      .filter(section => section)
      .reduce((sections, section) => {
        const filteredSection = {
          ...section,
          ...{ fields: section.fields.filter(f => f) },
        }
        return { ...sections, [section.name]: filteredSection }
      }, {})
  }

  mapContact = () => {
    const { contact, t, timezoneOverride, canUseFeature } = this.props
    const hasFeatureFlag = canUseFeature('qm.tenant.supportAvayaDecimalUcid')
    return {
      name: 'contact',
      title: t('contact'),
      fields: [
        this.contactDetail('contactId', contact.id),
        this.contactDetail('callingNumber', contact.ani, contact.ani),
        this.contactDetail('calledNumber', contact.dnis, !!contact.dnis),
        this.contactDetail('callDuration', toHumanReadable(moment.duration(contact.callDuration))),
        this.contactDetail('reason', t(contact.reason.key), !!contact.reason),
        this.contactDetail('hr', contact.hr ? t('yes') : t('no')),
        this.contactDetail('training', contact.training ? t('yes') : t('no')),
        this.contactDetail('state', t(evalScoreMap[contact.evalStateId ? contact.evalStateId : 0])),
        this.contactDetail('contactType', t(contact.contactType.toLowerCase())),
        contact.channel && this.contactDetail('channel', t(contact.channel)),
        this.contactDetail('recordingType', t(contact.recordingType.toLowerCase())),
        this.contactDetail(
          'endOfCallWorkflow',
          entities.decode(contact.endOfCallWorkflowRule),
          !!contact.endOfCallWorkflowRule,
        ),
        this.contactDetail('dailyWorkflow', entities.decode(contact.dailyWorkflowRule), !!contact.dailyWorkflowRule),
        //Associated Call ID is under 'related'
        this.contactDetail(
          t('videoFileUploadStateId'),
          t(formatMediaDisplayState(get(contact, 'screenDisplayStateId'))),
          this.getContactFileConditionValidity(contact, 'screenDisplayStateId'),
        ),
        this.contactDetail(
          t('audioFileUploadStateId'),
          t(formatMediaDisplayState(get(contact, 'audioDisplayStateId'))),
          this.getContactFileConditionValidity(contact, 'audioDisplayStateId'),
        ),
        this.contactDetail(
          'contactDirection',
          contact.isInbound ? t('inbound') : t('outbound'),
          typeof contact.isInbound === 'boolean',
        ),
        this.contactDetail('fromAddress', contact.fromAddress, !!contact.fromAddress),
        this.contactDetail('toAddress', contact.toAddress, !!contact.toAddress),
        this.contactDetail('subject', contact.subject, !!contact.subject),
        this.contactDetail('surveyScore', contact.surveyScore, !!contact.surveyScore),
        this.contactDetail('surveyName', contact.surveyName, !!contact.surveyName),
        this.contactDetail('tz', timezoneOverride ? timezoneOverride : contact.tz),
        hasFeatureFlag && this.contactDetail('avayaDecimalUCID', contact.avayaDecimalUCID, !!contact.avayaDecimalUCID),
        this.contactDetail('associatedContactId', contact.assocCallId, !!contact.assocCallId),
        this.contactDetail('retentionPolicyName', contact.retentionPolicyName),
        this.contactDetail('externalParty', contact.externalParty, !!contact.externalParty),
      ],
    }
  }

  getContactFileConditionValidity = (contact, prop) => {
    const fileUploadStateIsDefined = !isUndefined(contact[prop])
    const isVideoUpload = prop === 'screenDisplayStateId'
    const contactType = get(contact, 'contactType')
    const validContactTypes = isVideoUpload ? [CONTACT_TYPE.SCREEN_ONLY, CONTACT_TYPE.CALL] : [CONTACT_TYPE.CALL]

    return fileUploadStateIsDefined && validContactTypes.includes(contactType)
  }

  mapContactOrganizationDetails = () => {
    const { contact, file, t } = this.props
    return {
      name: 'organization',
      title: t('organization'),
      fields: [
        this.contactDetail('lastName', !file || !file.lastName ? contact.agent.lastName : file.lastName),
        this.contactDetail('firstName', !file || !file.firstName ? contact.agent.firstName : file.firstName),
        this.contactDetail('agentAcdId', contact.agent.agentAcdId, !!contact.agent.agentAcdId),
        this.contactDetail('group', !file || !file.group ? contact.group.name : file.group),
        this.contactDetail('team', !file || !file.team ? contact.team.name : file.team),
      ],
    }
  }

  mapContactEvaluationDetails = () => {
    const { contact, t } = this.props
    return contact.evaluation && contact.evalStateId === EVAL_STATE.SCORED
      ? {
        name: 'evaluation',
        title: t('evaluation'),
        fields: [
          this.contactDetail('percentageScore', Number.parseFloat(contact.evaluation.totalScore).toFixed(2)),
          this.contactDetail('evaluatorName', `${contact.evaluator.firstName} ${contact.evaluator.lastName}`),
          this.contactDetail(
            'dateEvaluated',
            contact.evaluation.evaluated === 32503593600000 && contact.evaluation.evaluatedTz === '#####'
              ? '####/##/##' // redacted
              : moment.tz(contact.evaluation.evaluated, contact.tz).format('MM/DD/YYYY'),
          ),
          this.contactDetail(
            'approvedBy',
            `${contact.approver.firstName ? contact.approver.firstName : ''} ${
              contact.approver.lastName ? contact.approver.lastName : ''
            }`,
            !!contact.approver,
          ),
          this.contactDetail('evaluationForm', contact.evalForm.name, !!contact.evalForm),
          this.contactDetail('calibrated', contact.isCalibration ? t('yes') : t('no')),
          this.contactDetail(
            'evalResponseDate',
            moment.tz(contact.evaluation.responseDate, contact.tz).format('MM/DD/YYYY'),
            !!contact.evaluation.responseDate,
          ),
          this.contactDetail(
            'evalResponseState',
            t(evalResponseStateMap[contact.evaluation.responseState]),
            !!contact.evaluation.responseState,
          ),
        ],
      }
      : null
  }

  mapContactSpeechDetails = () => {
    const { contact, t } = this.props
    return contact.contactType === CONTACT_TYPE.CALL
      ? {
        name: 'speech',
        title: t('speech'),
        fields: [
          this.contactDetail(
            'totalSilenceDuration',
            toHumanReadable(moment.duration(contact.eventCalculations.silence.totalDuration)),
          ),
          this.contactDetail('totalSilenceEvents', contact.eventCalculations.silence.totalEvents),
          this.contactDetail('percentSilenceEvents', `${contact.eventCalculations.silence.percentOfCall}%`),
          this.contactDetail(
            'totalTalkoverDuration',
            toHumanReadable(moment.duration(contact.eventCalculations.talkover.totalDuration)),
          ),
          this.contactDetail('totalTalkoverEvents', contact.eventCalculations.talkover.totalEvents),
          this.contactDetail('percentTalkoverEvents', `${contact.eventCalculations.talkover.percentOfCall}%`),
          this.contactDetail(
            'totalPauseDuration',
            toHumanReadable(moment.duration(contact.eventCalculations.pause.totalDuration)),
          ),
          this.contactDetail('pauseEvents', contact.eventCalculations.pause.totalEvents),
          this.contactDetail('percentPause', `${contact.eventCalculations.pause.percentOfCall}%`),
          this.contactDetail(
            'totalHoldDuration',
            toHumanReadable(moment.duration(contact.eventCalculations.hold.totalDuration)),
          ),
          this.contactDetail('holdEvents', contact.eventCalculations.hold.totalEvents),
          this.contactDetail('percentHold', `${contact.eventCalculations.hold.percentOfCall}%`),
        ],
      }
      : null
  }

  mapContactAnalyticsDetails = permitted => {

    const isAdvancedSentimentEnabled = this.props.canUseFeature(FEATURE_FLAGS.ANALYTICS_ENABLE_ADVANCED_SENTIMENT)
    const { contact, t } = this.props
    return permitted
      ? {
        name: 'analytics',
        title: t('analytics'),
        fields: [
          this.contactDetail(
            'PredictiveEvaluationScore',
            contact.evalScorePrediction,
            contact.evalScorePrediction || contact.evalScorePrediction === 0,
          ),
          this.contactDetail(
            'PredictiveNetPromoterScore',
            contact.netPromoterScorePrediction,
            contact.evalScorePrediction || contact.evalScorePrediction === 0,
          ),
          this.contactDetail(
            'sentiment',
            t(Sentiment.translationKey[Sentiment.getStateFromScore(Number.parseFloat(contact.sentiment))]),
            (contact.sentiment || contact.sentiment === 0) && !isAdvancedSentimentEnabled,
          ),
        ],
      }
      : null
  }

  renderContactSection = section => {
    const { classes } = this.props

    return (
      <div className={classes.contactInfoSection}>
        <Typography variant="body1" color="textPrimary" paragraph className={classes.contactInfoTitle}>
          {section.title}
        </Typography>
        {section.fields.map(({ key, label, value }) => (
          <div className={classes.contactInfoItem} key={key}>
            <Typography variant="body2" color="textSecondary" paragraph className={classes.contactInfoLabel}>
              {label}
            </Typography>
            <Typography
              auto-qa-id={`${autoQaIdPrefix}${section.name}-${key}`}
              data-testid={`${autoQaIdPrefix}${section.name}-${key}`}
              variant="body2"
              color="textPrimary"
              paragraph
              className={classes.contactInfoValue}
            >
              {value}
            </Typography>
          </div>
        ))}
      </div>
    )
  }

  openFindSimilar = () => this.setState({ isFindSimilarOpen: true })
  closeFindSimilar = () => this.setState({ isFindSimilarOpen: false })

  renderFindSimilar = () => {
    const {
      contact: { id },
      t,
      classes,
      openContact,
    } = this.props

    return (
      <CompactExpansionPanel
        defaultExpanded={false}
        classes={{ root: classes.expansionRoot }}
        auto-qa-id={`${autoQaIdPrefix}panel-FindSimilar`}
        data-testid={`${autoQaIdPrefix}panel-FindSimilar`}
      >
        <CompactExpansionPanelSummary
          classes={{
            root: classes.expansionSummaryRoot,
            expanded: classes.expansionSummaryExpanded,
          }}
          expandIcon={<BeamIcon>ChevronDown</BeamIcon>}
        >
          <Typography variant="subtitle2">{t('related')}</Typography>
        </CompactExpansionPanelSummary>
        <CompactExpansionPanelDetails classes={{ root: classes.expansionDetailsRoot }}>
          <div className={classes.details}>
            <div className={classes.findSimilarContainer}>
              <Button onClick={this.openFindSimilar} color="primary" auto-qa-id={`${autoQaIdPrefix}button-FindSimilar`} data-testid={`${autoQaIdPrefix}button-FindSimilar`}>
                {t('findSimilarContacts')}
              </Button>
            </div>
            <FindSimilar
              contactId={id}
              onClose={this.closeFindSimilar}
              open={this.state.isFindSimilarOpen}
              openContact={openContact}
            />
          </div>
        </CompactExpansionPanelDetails>
      </CompactExpansionPanel>
    )
  }

  renderLoading = centerClass => (
    <div className={centerClass}>
      <CircularProgress />
    </div>
  )

  renderError = (errorMessage, centerClass) => (
    <div className={centerClass}>
      <Typography color="secondary" align="center">
        {errorMessage}
      </Typography>
    </div>
  )

  renderData = () => {
    const {
      contact,
      classes,
      t,
      openContact,
      authenticatedUser: { hasPermission, hasAnyPermission },
      handleMetadataSubmission,
      hasDx,
    } = this.props

    const contactDetails = this.mapContactDetails()
    return (
      <Fragment>
        <AssociatedContacts contact={contact} onOpenContact={openContact} />
        {this.showGoalInstructions() && (
          <section>
            <Typography variant="h6" className={classes.sectionTitle}>
              {t('instructionsLabel')}
            </Typography>
            <Typography variant="body1" className={classes.goalInstructions}>
              {contact.goal.instruction}
            </Typography>
          </section>
        )}
        <section>
          <Typography variant="h6" className={classes.sectionTitle}>
            {t('contactInfoWidgetLabel')}
          </Typography>
          {contactDetails.contact &&
            contactDetails.contact.fields.length > 0 &&
            this.renderContactSection(contactDetails.contact, true)}
          {false /* TODO Remove false once Find Similar feature is fleshed out */ &&
            hasDx &&
            contact.contactType === CONTACT_TYPE.CALL &&
            this.renderFindSimilar()}
          {contactDetails.organization &&
            contactDetails.organization.fields.length > 0 &&
            this.renderContactSection(contactDetails.organization)}
          {contactDetails.evaluation &&
            contactDetails.evaluation.fields.length > 0 &&
            this.renderContactSection(contactDetails.evaluation)}
          {contactDetails.speech &&
            contactDetails.speech.fields.length > 0 &&
            this.renderContactSection(contactDetails.speech)}
          {contactDetails.analytics &&
            contactDetails.analytics.fields.length > 0 &&
            this.renderContactSection(contactDetails.analytics)}
          {hasPermission(PERMISSIONS.VIEW_CUSTOM_METADATA) && (
            <MetadataEditor
              contact={contact}
              canEditMetadata={hasPermission(PERMISSIONS.EDIT_CUSTOM_METADATA)}
              handleSubmission={handleMetadataSubmission}
            />
          )}
        </section>
        {hasAnyPermission([PERMISSIONS.ADMINISTER_QM_SURVEYS, PERMISSIONS.VIEW_SURVEY_RESULTS]) && (
          <PostCallSurvey contactId={contact.id} />
        )}
      </Fragment>
    )
  }

  render() {
    const {
      loading,
      error,
      contact,
      file = null,
      classes,
      t,
      openContact,
      authenticatedUser: { hasPermission },
      handleMetadataSubmission,
    } = this.props

    return (
      <Fragment>
        {(loading && this.renderLoading(classes.center)) ||
          (error && this.renderError(error, classes.center)) ||
          this.renderData({
            contact,
            file,
            classes,
            t,
            openContact,
            hasPermission,
            handleMetadataSubmission,
            renderFindSimilar: this.renderFindSimilar,
          })}
      </Fragment>
    )
  }
}

export default withFeatureFlags(withTranslation()(withStyles(styles)(withAuthenticatedUser(ExploreContent))))
